import React, { useCallback, useMemo, useState } from 'react';
import { Button, Column, Text } from '@ydistri/ds';
import { styled } from 'styled-components';
import { InputNumber } from 'antd';
import { useStableParams } from '../../../../routes/useStableParams';
import {
  useGetAsoStoreTierSettingsQuery,
  useUpdateAsoStoreTierSettingsMutation,
  useRecomputeAsoStoreTiersMutation,
} from '../../apiAso';
import { useAsoScenarioStatus } from '../../hooks/useAsoScenarioStatus';
import AsoTooltip from '../../AsoTooltip';
import { AsoValidationIssueMessages } from '../../validation/AsoValidationIssueMessages';
import {
  getAsoValidationIssueStatus,
  getAsoValidationIssuesForField,
  hasAsoValidationErrors,
  isAsoStoreTierRecomputeStateIssue,
  validateAsoStoreTierSettings,
} from '../../validation/asoValidation';
import { useAsoValidation } from '../../validation/useAsoValidation';

const RowLabel = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
`;

const ActionRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
`;

const InputStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const FixedWidthText = styled(Text)`
  display: inline-block;
  width: 8rem;
  flex: 0 0 8rem;
`;

const AsoStoreTierSettings: React.FC = () => {
  const { asoScenarioId } = useStableParams();
  const { isLocked } = useAsoScenarioStatus();
  const { issuesByArea } = useAsoValidation();

  const {
    data: settings,
    isLoading: isLoadingSettings,
    isError: isErrorSettings,
  } = useGetAsoStoreTierSettingsQuery(asoScenarioId ?? 0, {
    skip: !asoScenarioId,
  });
  const [updateSettings] = useUpdateAsoStoreTierSettingsMutation();
  const [recompute, { isLoading: isRecomputing }] = useRecomputeAsoStoreTiersMutation();

  const [tier1, setTier1] = useState<number | null | undefined>(undefined);
  const [tier2, setTier2] = useState<number | null | undefined>(undefined);

  const tier1Percent = tier1 !== undefined ? tier1 : (settings?.tier1Percent ?? null);
  const tier2Percent = tier2 !== undefined ? tier2 : (settings?.tier2Percent ?? null);

  const percentageIssues = useMemo(
    () =>
      validateAsoStoreTierSettings({
        tier1Percent,
        tier2Percent,
      }),
    [tier1Percent, tier2Percent],
  );

  const tier1Issues = useMemo(
    () => getAsoValidationIssuesForField(percentageIssues, 'tier1Percent'),
    [percentageIssues],
  );

  const percentageSummaryIssues = useMemo(
    () => percentageIssues.filter(issue => issue.key === 'storeTierPercentTotal'),
    [percentageIssues],
  );

  const tier2Issues = useMemo(
    () =>
      getAsoValidationIssuesForField(percentageIssues, 'tier2Percent').filter(
        issue => issue.key !== 'storeTierPercentTotal',
      ),
    [percentageIssues],
  );

  const recomputeStateIssues = useMemo(
    () => issuesByArea.storeTiers.filter(isAsoStoreTierRecomputeStateIssue),
    [issuesByArea.storeTiers],
  );

  const hasPercentageErrors = hasAsoValidationErrors(percentageIssues);

  const handleRecompute = useCallback(() => {
    if (!asoScenarioId || isLocked || hasPercentageErrors) return;
    const payload = {
      tier1Percent,
      tier2Percent,
    };

    updateSettings({ asoScenarioId, payload }).then(() => {
      recompute(asoScenarioId);
    });
  }, [
    asoScenarioId,
    hasPercentageErrors,
    isLocked,
    recompute,
    tier1Percent,
    tier2Percent,
    updateSettings,
  ]);

  return (
    <Column $gap="1rem">
      {isLoadingSettings && <Text>Loading settings…</Text>}
      {isErrorSettings && <Text $type="danger">Failed to load settings.</Text>}

      {!isLoadingSettings && !isErrorSettings && (
        <>
          <AsoValidationIssueMessages issues={recomputeStateIssues} />
          <RowLabel>
            <FixedWidthText as="label" htmlFor="aso-tier-1-percentage" $pointer>
              Tier 1 percentage
            </FixedWidthText>
            <AsoTooltip tooltipKey="tier1Percentage" />
            <InputStack>
              <InputNumber
                id="aso-tier-1-percentage"
                min={0}
                max={100}
                precision={0}
                step={1}
                status={getAsoValidationIssueStatus(tier1Issues)}
                addonAfter="%"
                value={tier1Percent ?? undefined}
                onChange={v => setTier1(typeof v === 'number' ? v : null)}
                disabled={isLocked || isRecomputing}
              />
              <AsoValidationIssueMessages issues={tier1Issues} />
            </InputStack>
          </RowLabel>

          <RowLabel>
            <FixedWidthText as="label" htmlFor="aso-tier-2-percentage" $pointer>
              Tier 2 percentage
            </FixedWidthText>
            <AsoTooltip tooltipKey="tier2Percentage" />
            <InputStack>
              <InputNumber
                id="aso-tier-2-percentage"
                min={0}
                max={100}
                precision={0}
                step={1}
                status={getAsoValidationIssueStatus(tier2Issues)}
                addonAfter="%"
                value={tier2Percent ?? undefined}
                onChange={v => setTier2(typeof v === 'number' ? v : null)}
                disabled={isLocked || isRecomputing}
              />
              <AsoValidationIssueMessages issues={tier2Issues} />
            </InputStack>
          </RowLabel>

          <AsoValidationIssueMessages issues={percentageSummaryIssues} />

          <ActionRow>
            <Button
              onClick={handleRecompute}
              loading={isRecomputing}
              disabled={!asoScenarioId || isLocked || isRecomputing || hasPercentageErrors}
            >
              Recompute store tiers
            </Button>
            <AsoTooltip tooltipKey="recomputeStoreTiers" />
          </ActionRow>
        </>
      )}
    </Column>
  );
};

export default AsoStoreTierSettings;
